import { NavLink } from 'react-router-dom'
import { accountNavFor } from '../lib/navigation'
import { Icon } from './Icon'
import { usePopover } from './usePopover'

/**
 * Who is signed in, and the configuration that belongs to them and to the organization.
 *
 * This is new. The console has never had an account menu - the identity was a flat strip with a
 * `Sign out` button beside it - so moving Administration and Platform here is building one rather
 * than relabeling one. Its three dismissal behaviors were ported from the group menus the masthead
 * used to render, and now live in `usePopover` so the help menu beside it dismisses the same way.
 *
 * **Every link stays in the DOM whether or not the menu is open.** `layout.spec.ts` reads the hrefs
 * out of both navigation landmarks without opening anything, which is the assertion that stops
 * "nothing scrolls sideways" being achieved by rendering half the product only on demand. Hiding the
 * list with `display: none` keeps it out of the tab order while keeping it findable.
 */
export function AccountMenu({
  displayName,
  tenantName,
  roles,
  onSignOut
}: {
  displayName: string
  tenantName: string | null
  roles: readonly string[]
  onSignOut: () => void
}) {
  // The dismissal rules live in the hook, shared with the help menu beside this one.
  const { open, setOpen, close, container, toggle, listId } = usePopover()

  const groups = accountNavFor(roles)

  return (
    <div className={`account-menu${open ? ' open' : ''}`} ref={container}>
      <button
        ref={toggle}
        type="button"
        className="account-toggle"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((was) => !was)}
        data-testid="account-menu"
      >
        <Icon name="user-circle" size={19} />
        <span className="account-name" data-testid="signed-in-as">
          {displayName}
        </span>
        <Icon name="caret-down" size={12} className="account-caret" />
      </button>

      {/*
        A second landmark rather than one nav spanning the shell. The rail is a grid area on the left
        and this is in the top bar, so a single <nav> would need `display: contents` - which has a
        history of dropping the implicit landmark out of the accessibility tree. Two named landmarks
        is better for a screen reader and just as strong for the gate, which takes the union.
      */}
      <nav id={listId} aria-label="Administration" className="account-panel" hidden={!open}>
        {tenantName && (
          <span className="account-tenant" data-testid="signed-in-tenant">
            {tenantName}
          </span>
        )}

        {groups.map((group) => (
          <div key={group.label} className="account-group">
            <span className="account-group-label">{group.label}</span>
            <ul>
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} onClick={close}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <button type="button" className="account-sign-out" onClick={onSignOut}>
          Sign out
        </button>
      </nav>
    </div>
  )
}
